import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from "firebase/auth";
import { firestore, auth, storage, functions } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import axios from "axios";
import DOMPurify from 'dompurify';
import { fetchParsedResumeText, handleResumeUpload } from './handleResume';
import FlipCard from './FlipCard';
import SubscriptionModal from './SubscriptionModal';
import './InterviewQuestion.css';
import ReactDOM from 'react-dom';
import { useUser } from '../contexts/UserContext';

const InterviewQuestion = () => {
  const [userId, setUserId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [step, setStep] = useState(1);
  const [currentResume, setCurrentResume] = useState(null);
  const [jobDescriptionText, setJobDescriptionText] = useState("");
  const [parsedJobDescription, setParsedJobDescription] = useState("");
  const [interviewQA, setInterviewQA] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { onSignupOpen } = useUser();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.isAnonymous) {
        setUserId(user.uid);
        setIsAuthenticated(true);

        // Check whether this user already has a parsed resume
        try {
          const resumeDocRef = doc(
            firestore,
            "uploads",
            user.uid,
            "parsedDocuments",
            "parsedResume"
          );
          const resumeSnap = await getDoc(resumeDocRef);
          setResumeUploaded(resumeSnap.exists());
        } catch (err) {
          console.error("Error checking for existing resume:", err);
          setResumeUploaded(false);
        }
      } else {
        setUserId(null);
        setIsAuthenticated(false);
        setResumeUploaded(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const parseFile = async (fileUrl) => {
    try {
      const response = await axios.post(
        "https://api-whiywkgf4q-uc.a.run.app/parseFile",
        { url: fileUrl }
      );
      return response.data.text;
    } catch (error) {
      console.error("Error parsing file:", error);
      return null;
    }
  };

  const handleSubscriptionSuccess = () => {
    setShowSubscriptionModal(false);
    // you might refresh user entitlements here
  };

  const handleRequireSignup = () => {
    onSignupOpen();
    setShowSubscriptionModal(false);
  };

  // Function to handle resume selection (Step 1)
  const handleResumeSelection = async (useCurrentResume) => {
    // Unauthenticated users always get the payment modal on upload-click,
    // and authenticated users without a stored resume can't "Use Current"
    if (!useCurrentResume && !isAuthenticated) {
      setShowSubscriptionModal(true);
      return;
    }

    setLoading(true);
    setProgress(useCurrentResume ? 'Fetching resume...' : 'Uploading resume...');
    try {
      if (useCurrentResume) {
        const resume = await fetchParsedResumeText(userId);
        if (!resume) throw new Error("No current resume found.");
        setCurrentResume(resume);
        setStep(2);
      } else {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf,.docx';
        fileInput.onchange = async (event) => {
          const file = event.target.files[0];
          if (file) {
            setProgress('Uploading resume...');
            const resumeUrl = await handleResumeUpload(file, userId);

            setProgress('Parsing resume...');
            const parsedData = await parseFile(resumeUrl, file.type);
            if (!parsedData) throw new Error("Failed to parse the resume.");

            // Save parsed resume to Firestore
            const userDocRef = doc(
              firestore,
              "uploads",
              userId,
              "parsedDocuments",
              "parsedResume"
            );
            await setDoc(userDocRef, {
              type: "resume",
              content: parsedData,
              timestamp: new Date(),
            });

            setCurrentResume(parsedData);
            setResumeUploaded(true);
            setStep(2);
          }
        };
        fileInput.click();
      }
    } catch (error) {
      console.error("Error selecting resume:", error);
      alert(error.message);
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  const handleJobDescriptionFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setProgress("Parsing job description...");
    try {
      const fileUrl = await handleResumeUpload(file, userId);
      const parsed = await parseFile(fileUrl);
      if (!parsed) throw new Error("Failed to parse job description.");
      setParsedJobDescription(parsed);
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  const fetchInterviewQA = async () => {
    if (!currentResume) {
      alert("Please provide your resume.");
      return;
    }
    const jobDesc = parsedJobDescription || jobDescriptionText;
    if (!jobDesc || jobDesc.length < 100) {
      alert("Please provide a valid job description (at least 100 characters).");
      return;
    }
    setLoading(true);
    setProgress("Generating interview questions...");
    try {
      const safeDesc = DOMPurify.sanitize(jobDesc);
      const callable = httpsCallable(functions, 'generateInterviewQA');
      const response = await callable({ resume: currentResume, jobDescription: safeDesc });
      if (response.data.status === "LIMIT_REACHED") {
        setShowSubscriptionModal(true);
        return;
      }
      const qaList = response.data.qa || response.data;
      setInterviewQA(qaList);
      setStep(3); // Move to Step 3 (Display Interview Questions)
    } catch (error) {
      console.error("Error fetching interview Q&A:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  return (
    <div className="interview-question-generator">
      {loading && (
        <div className="loading-indicator">
          <p>{progress}</p>
        </div>
      )}

      {/* subscription modal portal */}
      {showSubscriptionModal &&
        ReactDOM.createPortal(
          <div className="subscription-overlay">
            <div className="subscription-modal">
              <button
                className="modal-close"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
              <SubscriptionModal
                isOpen={true}
                onClose={() => setShowSubscriptionModal(false)}
                onSubscriptionSuccess={handleSubscriptionSuccess}
                onRequireSignup={handleRequireSignup}
                productType="interviewQuestions"
              />
            </div>
          </div>,
          document.getElementById('modal-root')
        )
      }

      {step === 1 && (
        <div className="step step-1">
          <h2>Select Your Resume</h2>
          <div className="button-group">
            {isAuthenticated && resumeUploaded && (
              <button onClick={() => handleResumeSelection(true)}>
                Use Current Resume
              </button>
            )}
            <button onClick={() => handleResumeSelection(false)}>
              Upload Resume
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="step step-2">
          <h2>Provide Job Description</h2>
          <div className="job-description-options">
            <div className="job-description-paste">
              <p>Or Paste Text:</p>
              <textarea
                value={jobDescriptionText}
                onChange={(e) => setJobDescriptionText(e.target.value)}
                placeholder="Paste the job advert or position description here..."
              />
            </div>
            <div className="job-description-upload">
              <p>Upload File:</p>
              <input type="file" accept=".pdf,.docx" onChange={handleJobDescriptionFileUpload} />
            </div>
          </div>
          <button className="next-button" onClick={fetchInterviewQA}>
            Generate Interview Questions
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="step step-3">
          <h2>Your Interview Questions</h2>
          <div className='flipcard-container'>
            <div className="flipcard-grid">
                {interviewQA.map((item, idx) => (
                <FlipCard key={idx} question={item.question} answer={item.answer} />
                ))}
            </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default InterviewQuestion;
